// src/routes/AuctionTags.tsx
//
// RFA Phase 4 §4.2 — the pre-auction franchise-tag picker.
//
// While the auction is 'pre', each owner sees their own eligible players
// (view.players filtered to priorFranchiseId === me) with Tag/Remove buttons.
// After lock, the page shows a "Tags locked" banner and points at /auction.
// The public "All tags" panel renders in every status.
//
// Mock-only (post-dry-run revision 2026-07-25): when the viewer is the
// commissioner AND the auction is a mock, the page instead lists EVERY
// franchise's eligible players grouped by franchise, so a solo dry run can
// rehearse tags — buttons pass the target franchiseId to setTag/unsetTag.
import { useMutation, useQuery } from 'convex/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

export default function AuctionTags() {
  const me = useQuery(api.franchises.myFranchise);
  const current = useQuery(api.rfa.init.getCurrentAuction);
  const view = useQuery(
    api.rfa.auction.getAuctionView,
    current ? { auctionId: current._id } : 'skip',
  );
  // Balance context for the deposit hint + per-franchise balances in the
  // commissioner's mock group view (one existing subscription each).
  const myBudget = useQuery(
    api.rfa.budget.getMyAuctionBudget,
    current ? { auctionId: current._id } : 'skip',
  );
  const budgets = useQuery(
    api.rfa.budget.getAuctionBudgets,
    // Only the commissioner's mock group view renders other balances.
    current && current.isMock === true ? { auctionId: current._id } : 'skip',
  );
  const setTag = useMutation(api.rfa.tags.setTag);
  const unsetTag = useMutation(api.rfa.tags.unsetTag);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (me === undefined || current === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        <p className="mt-2 text-sm text-gray-600">
          Claim a franchise first.{' '}
          <a className="underline" href="/franchises">
            Pick one →
          </a>
        </p>
      </div>
    );
  }
  if (current === null) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold">Franchise tags</h1>
        <p className="mt-2 text-sm text-gray-600">No auction is running.</p>
      </div>
    );
  }
  if (view === undefined) return <div className="p-6">Loading…</div>;

  const isPre = view.auction.status === 'pre';
  const myTag = view.tags.find((t) => t.franchiseId === me._id) ?? null;
  const myPlayers = view.players.filter((p) => p.priorFranchiseId === me._id);

  // Mock-only commissioner mode: rehearse every team's tag from one screen
  // (setTag/unsetTag accept a mock-only "act as" franchiseId).
  const commishMockTagging = view.auction.isMock && me.isCommissioner === true;
  const franchiseGroups = new Map<
    Id<'franchises'>,
    { franchiseName: string; players: typeof view.players }
  >();
  if (commishMockTagging) {
    for (const p of view.players) {
      const group = franchiseGroups.get(p.priorFranchiseId) ?? {
        franchiseName: p.priorFranchiseName,
        players: [],
      };
      group.players.push(p);
      franchiseGroups.set(p.priorFranchiseId, group);
    }
  }
  const sortedGroups = [...franchiseGroups.entries()].sort((a, b) =>
    a[1].franchiseName.localeCompare(b[1].franchiseName),
  );

  const onTag = async (playerId: Id<'players'>, franchiseId?: Id<'franchises'>) => {
    setError(null);
    setSubmitting(true);
    try {
      await setTag({
        auctionId: current._id,
        playerId,
        ...(franchiseId !== undefined ? { franchiseId } : {}),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onRemoveTag = async (franchiseId?: Id<'franchises'>) => {
    setError(null);
    setSubmitting(true);
    try {
      await unsetTag({
        auctionId: current._id,
        ...(franchiseId !== undefined ? { franchiseId } : {}),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold">Franchise tags — RFA {view.auction.season}</h1>

      {view.auction.isMock && (
        <div className="mt-3 rounded border-2 border-amber-500 bg-amber-100 px-4 py-3 text-sm font-bold text-amber-900">
          ⚠ MOCK AUCTION — practice mode, nothing is real. Results are discarded on reset. Budgets
          start from your real balance.
        </div>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {isPre && commishMockTagging ? (
        <>
          <div className="mt-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
            Mock: as commissioner you can set any team's tag. A tag costs a{' '}
            <strong>10-bone deposit</strong> (virtual here — nothing is charged for real), charged
            once per team per auction and <strong>non-refundable</strong> — returned only if the
            tagged player is sold to another franchise. Untagging does not refund it (re-tagging is
            free), and it stays spent on a match, an unclaimed-at-0 retention, or a self-bought
            player. Tagged players open at <strong>22 bones minimum</strong>, bid in increments of
            2, and cost the prior owner half the winning bid (rounded up) to match.
          </div>

          {sortedGroups.map(([franchiseId, group]) => {
            const groupTag = view.tags.find((t) => t.franchiseId === franchiseId) ?? null;
            const groupBalance = budgets?.find((b) => b.franchiseId === franchiseId)?.balance;
            return (
              <section key={franchiseId} className="mt-6">
                <h2 className="font-semibold mb-2">
                  {group.franchiseName}
                  {franchiseId === me._id && (
                    <span className="ml-1 text-xs text-blue-600">(you)</span>
                  )}
                  {groupTag ? (
                    <span className="ml-2 text-sm font-normal text-gray-600">
                      — tagged: <strong>{groupTag.playerName}</strong>
                    </span>
                  ) : (
                    <span className="ml-2 text-sm font-normal text-gray-400">— no tag</span>
                  )}
                  {groupBalance !== undefined && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      · balance {groupBalance}
                    </span>
                  )}
                </h2>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="py-1">Player</th>
                      <th>Pos</th>
                      <th>Team</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {group.players.map((p) => {
                      const isGroupTag = groupTag?.playerId === p.playerId;
                      return (
                        <tr key={p.rfaPlayerId} className="border-b">
                          <td className="py-1.5 font-medium">
                            {p.name}
                            {isGroupTag && (
                              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
                                TAGGED
                              </span>
                            )}
                          </td>
                          <td>{p.position}</td>
                          <td>{p.nflTeam}</td>
                          <td className="text-right">
                            {isGroupTag ? (
                              <button
                                type="button"
                                onClick={() => onRemoveTag(franchiseId)}
                                disabled={submitting}
                                className="text-sm text-red-600 hover:underline disabled:opacity-50"
                              >
                                Remove tag
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onTag(p.playerId, franchiseId)}
                                disabled={submitting}
                                className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                              >
                                {groupTag ? 'Move tag here' : 'Tag (10 bones)'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            );
          })}
        </>
      ) : isPre ? (
        <>
          <div className="mt-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
            {myTag ? (
              <>
                Your franchise tag: <strong>{myTag.playerName}</strong> (10-bone deposit paid —
                <strong> non-refundable</strong>: returned only if your tagged player is sold to
                another franchise. Removing or moving the tag does not refund it, and it stays spent
                on a match, an unclaimed-at-0 retention, or buying your own player). Tagged players
                open at <strong>22 bones minimum</strong>, bid in increments of 2, and cost you half
                the winning bid (rounded up) to match.
              </>
            ) : (
              <>
                No tag set. You may franchise-tag <strong>one</strong> of your expiring players
                before the commissioner locks tags. The tag costs a <strong>10-bone deposit</strong>
                {view.auction.isMock ? ' (virtual in this mock)' : ''}, charged once per auction and{' '}
                <strong>non-refundable</strong> — returned only if your tagged player is sold to
                another franchise (untagging does not refund it; re-tagging later is free).
              </>
            )}
            {myBudget !== undefined && (
              <>
                {' '}
                Your balance: <strong>{myBudget.balance}</strong> bones.
              </>
            )}
          </div>

          <section className="mt-6">
            <h2 className="font-semibold mb-2">My eligible players</h2>
            {myPlayers.length === 0 ? (
              <p className="text-sm text-gray-500">
                None of your players are in this year's RFA pool.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-1">Player</th>
                    <th>Pos</th>
                    <th>Team</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {myPlayers.map((p) => {
                    const isMyTag = myTag?.playerId === p.playerId;
                    return (
                      <tr key={p.rfaPlayerId} className="border-b">
                        <td className="py-1.5 font-medium">
                          {p.name}
                          {isMyTag && (
                            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
                              TAGGED
                            </span>
                          )}
                        </td>
                        <td>{p.position}</td>
                        <td>{p.nflTeam}</td>
                        <td className="text-right">
                          {isMyTag ? (
                            <button
                              type="button"
                              onClick={() => onRemoveTag()}
                              disabled={submitting}
                              className="text-sm text-red-600 hover:underline disabled:opacity-50"
                            >
                              Remove tag
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onTag(p.playerId)}
                              disabled={submitting}
                              className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                            >
                              {myTag ? 'Move tag here' : 'Tag (10 bones)'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : (
        <div className="mt-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
          Tags are locked.{' '}
          <Link className="underline" to="/auction">
            Go to the auction room →
          </Link>
        </div>
      )}

      <section className="mt-8">
        <h2 className="font-semibold mb-2">All tags</h2>
        {view.tags.length === 0 ? (
          <p className="text-sm text-gray-500">No franchise tags set.</p>
        ) : (
          <ul className="text-sm space-y-0.5">
            {view.tags.map((t) => (
              <li key={`${t.franchiseId}-${t.playerId}`}>
                <span className="font-medium">{t.franchiseName}</span> → <span>{t.playerName}</span>
                {t.franchiseId === me._id && (
                  <span className="ml-2 text-xs text-blue-600">(you)</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
